import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Button,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputAdornmentProps,
  ListItemText,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import { lighten, darken, SxProps, type Theme } from '@mui/material/styles';
import { DateTimePicker } from '@mui/x-date-pickers';
import * as React from 'react';
import {
  CSSProperties,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { TFunction, useTranslation } from 'react-i18next';
import { useAuth } from 'oidc-react';
import TitledDrawer from '../../../components/TitledDrawer';
import {
  comboStyle,
  DEFAULT_BUSINESS_START_HOUR,
  desktopButtonStyle,
  desktopSecondaryButtonStyle,
} from '../../../constants';
import { dateRange, DateRangeConfig, DayOfWeek, TInputDateToString } from '../../../helpers/dateUtils';
import { DeviceGroup, DeviceType, useCompanyConfig } from '../../../api/companyConfig';
import TimeZoneAccordion from './TimeZoneAccordion';
import { SettingsContext } from '../../../context/SettingsContext';
import ValidationError from '../../../models/validationError';
import { CreateReportBody, ReportOverviewItem, createReport } from '../../../api/reportOverview';
import { createReportSchedule, ScheduleRequestBody, updateReportSchedule } from '../../../api/generatedReports';
import { Location, LocationArea } from '../../../models/filters';
import { TransactionDevice, TransactionStaff, TransactionTenderType } from '../../../models/transactions';
import { ReportType } from '../../../models/reportOverviewRow';
import { Report } from '../../../models/report';
import { UserContext } from '../../../context/UserContext';

const DEFAULT_RECURRENCE_VALUE_KEY = 'default';

const subHeaderStyle: CSSProperties = {
  fontFamily: 'Open Sans',
  fontStyle: 'normal',
  fontWeight: 600,
  fontSize: '16px',
};

const scrollCSS: SxProps<Theme> = {
  '&::-webkit-scrollbar': {
    width: '5px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '10px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#888',
    borderRadius: '10px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#555',
  },
};

const selectMenuScrollProps = {
  PaperProps: {
    sx: scrollCSS,
  },
  MenuListProps: {
    disablePadding: true,
  },
};

const stickyAllSelectMenuItemSx: SxProps<Theme> = {
  position: 'sticky',
  top: 0,
  zIndex: 1,
  bgcolor: (theme: Theme) => theme.palette.background.paper,
  backgroundImage: 'none',
  '&.Mui-selected': {
    bgcolor: (theme: Theme) =>
      theme.palette.mode === 'dark'
        ? darken(theme.palette.primary.main, 0.88)
        : lighten(theme.palette.primary.main, 0.92),
  },
  '&:hover': {
    bgcolor: (theme: Theme) =>
      theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
  },
  '&.Mui-selected:hover': {
    bgcolor: (theme: Theme) =>
      theme.palette.mode === 'dark'
        ? darken(theme.palette.primary.main, 0.84)
        : lighten(theme.palette.primary.main, 0.88),
  },
};

interface GenerateReportForm {
  name: string;
  reportTypeId: number;
  scheduled?: boolean;
  recurrence?: string;
  startDate: string;
  endDate: string;
  location: Location;
  locationArea: LocationArea;
  staff: TransactionStaff;
  tenderType: TransactionTenderType;
  device: TransactionDevice;
  deviceGroup?: DeviceGroup;
  deviceType?: DeviceType;
  fileType: string;
}


const bottomToolbar = (t: TFunction<"translation", undefined>, onAccept: () => void, close: () => void, scheduleId?: string) => (

  <Box>
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row-reverse',
        marginLeft: 'auto',
        padding: '16px'
      }}
    >
      <Button
        variant="contained"
        sx={[desktopButtonStyle]}
        onClick={onAccept}
        autoFocus
      > {scheduleId ? t('actions.save') : t('screens.dataDownloader.generate')}
      </Button>
      <Button
        variant="text"
        sx={{ ...desktopSecondaryButtonStyle }}
        onClick={close}
      >
        {t('screens.dataDownloader.cancel')}
      </Button>
    </Box>
  </Box>
);

const createReportRequestBody = (form: GenerateReportForm, displayKey: string, reportTypeId: number, locale: string): CreateReportBody => ({
  name: form.name ? form.name : displayKey,
  reportTypeId,
  startDate: new Date(form.startDate),
  endDate: new Date(form.endDate),
  locale, 
  filters: JSON.stringify({
    locationIds: form.location ? [Number.parseInt(form.location.id, 10)] : [],
    locationAreaIds: form.locationArea ? [Number.parseInt(form.locationArea.id, 10)] : [],
    staffIds: form.staff ? [Number.parseInt(form.staff.id, 10)] : [],
    tenderTypeIds: form.tenderType ? [Number.parseInt(form.tenderType.id, 10)] : [],
    deviceIds: form.device ? [Number.parseInt(form.device.id, 10)] : [],
    deviceGroupIds: form.deviceGroup ? [form.deviceGroup.id] : [],
    deviceTypeIds: form.deviceType ? [form.deviceType.id] : [],
  }),
  fileType: "csv"
});

const createScheduleRequestBody = (
  form: GenerateReportForm,
  displayKey: string,
  reportTypeId: number,
  timezone: string,
): ScheduleRequestBody => ({
  name: form.name ? form.name : displayKey,
  reportTypeId,
  startDate: form.startDate,
  endDate: form.endDate,
  recurrence: form.recurrence || 'daily',
  timezone,
  locationIds: form.location ? [Number.parseInt(form.location.id, 10)] : [],
  locationAreaIds: form.locationArea ? [Number.parseInt(form.locationArea.id, 10)] : [],
  staffIds: form.staff ? [Number.parseInt(form.staff.id, 10)] : [],
  tenderTypeIds: form.tenderType ? [Number.parseInt(form.tenderType.id, 10)] : [],
  deviceIds: form.device ? [Number.parseInt(form.device.id, 10)] : [],
  fileType: 'csv',
});

type ReportSourceData = Report | {
  id: number;
  displayKey: string;
  isManual?: boolean;
  isAutomated?: boolean;
  type?: ReportType;
};

interface GenerateReportOverviewPanelProps {
  close: (snackbarMessage?: { message: string, severity: 'success' | 'error' }) => void;
  reportSource: ReportSourceData;
  reportTypes?: ReportOverviewItem[];
}

const GenerateReportOverviewPanel = ({ reportSource, close, reportTypes }: GenerateReportOverviewPanelProps) => {
  const { locale } = useContext(UserContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isReport = 'reportType' in reportSource;
  const enableScheduledFeature = process.env.REACT_APP_ENABLE_DOWNLOADER_SCHEDULED_FEATURE === 'true';
  const scheduleId = isReport ? reportSource.id : undefined;
  const reportRowData = React.useMemo(
    () => isReport ? { id: reportSource.reportType?.id, isAutomated: reportSource.isAutomated } : { id: reportSource.id, isManual: reportSource.isManual, isAutomated: reportSource.isAutomated },
    [reportSource, isReport]
  );
  const reportDisplayKey = isReport ? reportSource.name : t(`screens.dataDownloader.reportType.${reportSource.displayKey}`);
  const reportType: ReportType = isReport ? 'Scheduled' : reportSource.type ?? 'Manual';

  const availableFilters = useMemo(() => {
    const reportTypeId = isReport ? reportSource?.reportType?.id : reportSource.id;

  const foundReportType = reportTypes?.find((rt) => rt.id === reportTypeId);

    return foundReportType?.availableFilters;
  }, [reportSource, reportTypes, isReport]);

  const { data: formData } = useCompanyConfig(`generatedReportConfig`);

  const getInitialRecurrence = () => {
    if (isReport) {
      return reportSource.recurrence as string;
    }
    return reportType === 'Scheduled' ? DEFAULT_RECURRENCE_VALUE_KEY : undefined;
  };
  const { isLoadingCalendarPreferences, calendarPreferences } = useContext(SettingsContext);

  const dateRangeConfig: DateRangeConfig = useMemo(() => ({
    weeksStartOn: isLoadingCalendarPreferences ? "monday" : calendarPreferences.weeksStart as unknown as DayOfWeek,
    daysStartAt: isLoadingCalendarPreferences ? DEFAULT_BUSINESS_START_HOUR : calendarPreferences.daysStart,
    useLocalTime: true
  }), [isLoadingCalendarPreferences, calendarPreferences]);
  const twoYearsAgo = new Date();
  twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);

const firstValidEndDate = new Date();
firstValidEndDate.setFullYear(firstValidEndDate.getFullYear() - 2);

const firstValidStartDate = new Date(firstValidEndDate);
firstValidStartDate.setMonth(firstValidStartDate.getMonth() - 1);


  const [form, setForm] = useState<GenerateReportForm>({
    scheduled: reportType === 'Scheduled',
    name: reportDisplayKey,
    startDate: isReport
      ? reportSource.filters?.startDate ?? TInputDateToString(firstValidStartDate)
      : TInputDateToString(firstValidStartDate),
    endDate: isReport
      ? reportSource.filters?.endDate ?? TInputDateToString(firstValidEndDate)
      : TInputDateToString(firstValidEndDate),
    recurrence: getInitialRecurrence(),
  } as GenerateReportForm);

  const formatIdsToNumber = (data: any) => {
    if (!data) return data;
    const copy = { ...data };
    Object.keys(copy).forEach((key) => {
      if (key.toLowerCase().endsWith('id')) {
        copy[key] = Number.parseInt(copy[key], 10);
      }
    });
    return copy;
  };

  useEffect(() => {
    if (!formData) return;
    if (!isReport) return;
    setForm((prev: GenerateReportForm) => ({
      ...prev,
      location: formatIdsToNumber(reportSource.filters?.locations?.[0]),
      locationArea: formatIdsToNumber(reportSource.filters?.locationAreas?.[0]),
      staff: formatIdsToNumber(reportSource.filters?.staff?.[0]),
      tenderType: formatIdsToNumber(reportSource.filters?.tenderTypes?.[0]),
      device: formatIdsToNumber(reportSource.filters?.devices?.[0]),
      deviceGroup: reportSource.filters?.deviceGroups?.[0],
      deviceType: reportSource.filters?.deviceTypes?.[0],
    }));
  }, [formData, reportSource, isReport]);
  const [timezone, setTimezone] = useState('Europe/London');
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const auth = useAuth();
  const maybeAccessToken = auth?.userData?.access_token;
  const isManual = reportRowData?.isManual ?? false;
  const isAutomated = reportRowData?.isAutomated ?? false;

  const validateForm = useCallback(() => {
    const errors = [];

    if (!form.name) {
      errors.push({ field: "name", message: t('validation.fieldRequired', { field: t('screens.dataDownloader.generatedReportsTable.name') }) });
    }

    const validFrom = !Number.isNaN(Date.parse(form.startDate || ""));
    const validTo = !Number.isNaN(Date.parse(form.endDate || ""));
    const allowAllDates = process.env.REACT_APP_DISABLE_DOWNLOADER_TIME_RESTRICTIONS === 'true';
    const isWithinTwoYearRange = {
      from: validFrom && (new Date(form.startDate) >= twoYearsAgo && !allowAllDates),
      to: validTo && (new Date(form.endDate) >= twoYearsAgo && !allowAllDates),
    }
    let dateGap;

    if (availableFilters?.includes('time')) {
      if (!validFrom) {
        errors.push({ field: "periodFrom", message: t("components.datePicker.enterValidDate") });
      }
      if (!validTo) {
        errors.push({ field: "periodTo", message: t("components.datePicker.enterValidDate") });
      }
      if (validFrom && validTo) {
        dateGap = new Date(form.endDate || "").getTime() - new Date(form.startDate || "").getTime()
      }
      if (dateGap && dateGap < 0) {
        errors.push({ field: "periodFrom", message: t("components.datePicker.startDateMayNotBeAfterEnd") });
      }
      if (dateGap === 0) {
        errors.push({ field: "periodTo", message: t("components.datePicker.endDateMayNotBeBeforeStart") });
      }
      if (isWithinTwoYearRange.from) {
        errors.push({ field: "periodFrom", message: t("components.datePicker.startDateMustBeOlderThanTwoYears") });
      }
      if (isWithinTwoYearRange.to) {
        errors.push({ field: "periodTo", message: t("components.datePicker.endDateMustBeOlderThanTwoYears") });
      }
    }

    if (form.scheduled && (!form.recurrence || form.recurrence === DEFAULT_RECURRENCE_VALUE_KEY)) {
      errors.push({ field: "recurrence", message: t("components.filters.recurrence.selectRecurrenceErrorMessage") });
    }

    setValidationErrors(errors);
    return errors.length === 0;
  }, [form, setValidationErrors, t]);

  const errorsFor = useCallback(
    (key: string) =>
      validationErrors
        .filter((it) => it.field === key)
        .map((it) => it.message)
        .join(', '),
    [validationErrors]
  );
  const hasErrors = useCallback((key: string) => errorsFor(key).length > 0, [errorsFor]);

  const onChangeStartDate = useCallback(
    (date: Date | undefined | null) =>
      setForm(
        (prevForm: GenerateReportForm) =>
        ({
          ...prevForm,
          startDate: TInputDateToString(date),
        } as GenerateReportForm)
      ),
    [setForm]
  );

  const onChangeEndDate = useCallback(
    (date: Date | undefined | null) =>
      setForm(
        (prevForm: GenerateReportForm) =>
        ({
          ...prevForm,
          endDate: TInputDateToString(date),
        } as GenerateReportForm)
      ),
    [setForm]
  );

  const [openPeriodFrom, setOpenStartDate] = useState(false);
  const [openPeriodTo, setOpenEndDate] = useState(false);

  const pickerStartDate = useMemo(
    () => (form.startDate ? new Date(form.startDate) : new Date()),
    [form.startDate]
  );
  const pickerEndDate = useMemo(
    () => (form.endDate ? new Date(form.endDate) : new Date()),
    [form.endDate]
  );

  const customStartPicker = useCallback(
    (p: InputAdornmentProps) => (
      <InputAdornment {...p} position="end" onClick={() => setOpenStartDate(true)}>
        <IconButton aria-label="Period from (show calendar)" sx={{ left: '12px' }}>
          {hasErrors('periodFrom') ? <ErrorOutlineIcon color="error" /> : <EventOutlinedIcon />}
        </IconButton>
      </InputAdornment>
    ),
    [setOpenStartDate, hasErrors]
  );

  const customEndPicker = useCallback(
    (p: InputAdornmentProps) => (
      <InputAdornment {...p} position="end" onClick={() => setOpenEndDate(true)}>
        <IconButton aria-label="Period to (show calendar)" sx={{ left: '12px' }}>
          {hasErrors('periodTo') ? <ErrorOutlineIcon color="error" /> : <EventOutlinedIcon />}
        </IconButton>
      </InputAdornment>
    ),
    [setOpenEndDate, hasErrors]
  );

  const fireResize = useCallback(() => {
    if (typeof Event === 'function') {
      // modern browsers
      window.dispatchEvent(new Event('resize'));
    } else {
      // for IE and other old browsers
      // causes deprecation warning on modern browsers
      const evt = window.document.createEvent('UIEvents');
      evt.initUIEvent('resize', true, false, window, 0);
      window.dispatchEvent(evt);
    }
  }, []);


  const generateReport = useCallback(async () => {
    if (!validateForm()) {
      return false;
    }
    setValidationErrors([]);
    const isScheduledEdit = Boolean(scheduleId);
    if (!reportRowData) return false;

    try {
      if (isScheduledEdit) {
        const schedule = createScheduleRequestBody(form, reportDisplayKey, reportRowData.id, timezone);
        await updateReportSchedule(scheduleId as string, schedule, maybeAccessToken);
      } else if (form.scheduled) {
        const schedule = createScheduleRequestBody(form, reportDisplayKey, reportRowData.id, timezone);
        await createReportSchedule(schedule, maybeAccessToken);
      } else {
        const reportBody = createReportRequestBody(form, reportDisplayKey, reportRowData.id, locale);
        await createReport(reportBody, maybeAccessToken);
      }
      close({
        message: isScheduledEdit
          ? t('screens.dataDownloader.updateSuccess')
          : t('screens.dataDownloader.reportGenerationStarted', { name: form.name || reportDisplayKey }),
        severity: 'success'
      });
      // Redirect to reports-hub after successful generation
      if (!isScheduledEdit) {
        navigate('/reports-hub');
      }
    } catch (e) {
      close({
        message: t('components.error.somethingFailed'),
        severity: 'error'
      });
    }
    return true;
  }, [validateForm, setValidationErrors, form, reportDisplayKey, reportRowData, maybeAccessToken, scheduleId, timezone, close, navigate, t]);

  const hasTimeFilters = form?.scheduled || availableFilters?.includes('time'); 
  const hasLocationFilters =
    availableFilters?.includes('location') ||
    availableFilters?.includes('locationArea');
  const hasAdvancedFilters =
    availableFilters?.includes('device') ||
    availableFilters?.includes('deviceGroup') ||
    availableFilters?.includes('deviceType') ||
    availableFilters?.includes('paymentMethod') ||
    availableFilters?.includes('staff');

  return (
    <TitledDrawer
      title={ isReport ? t('actions.edit') : t('screens.dataDownloader.generate')}
      onClose={() => close()}
      bottomToolbar={bottomToolbar(t, generateReport, close, scheduleId)}
      bottomToolbarMarginTop='16px'
    >
      <Grid
        item
        sx={{
          paddingLeft: '4px',
          paddingRight: '4px',
        }}
      >
        <TextField
          size="small"
          value={form.name}
          label={reportDisplayKey}
          sx={comboStyle}
          required
          error={hasErrors('name')}
          helperText={errorsFor('name')}
          onChange={(ev) =>
            setForm((prev) => ({
              ...prev,
              name: ev.target.value,
            }))
          }
        />
        {isManual && isAutomated && enableScheduledFeature && (
          <FormControlLabel
            control={
              <Switch
                checked={form.scheduled || false}
                onChange={(ev) => {
                  const {checked} = ev.target;
                  setForm((prev) => 
                    checked
                      ? { ...prev, scheduled: true, recurrence: DEFAULT_RECURRENCE_VALUE_KEY }
                      : { ...prev, scheduled: false, recurrence: undefined }
                  );
                }}
              />
            }
            label={<Typography sx={subHeaderStyle} variant="h3">{t('components.filters.scheduled')}</Typography>}
            labelPlacement="start"
            sx={{ marginLeft: '0', width: '100%', justifyContent: 'space-between', paddingY: '2px' }}
          />
        )}
        {hasTimeFilters && (
          <Accordion
            elevation={0}
            defaultExpanded
            disableGutters
            square
            sx={{
              padding: 0,
              border: 0,
              margin: 0,
            }}
            onClick={() => setTimeout(fireResize, 250)}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="timeFilters-content"
              sx={{ padding: 0, border: 0, margin: 0 }}
              id="timeFilters-header"
            >
              <Typography sx={subHeaderStyle} variant="h3">
                {t('components.filters.time')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 0, border: 0, margin: 0 }}>
              {form.scheduled && (
                <TextField
                  size="small"
                  select
                  value={form.recurrence || DEFAULT_RECURRENCE_VALUE_KEY}
                  label={t('components.filters.recurrence.label')}
                  sx={{ ...comboStyle, minWidth: 180, marginBottom: 2 }}
                  error={hasErrors('recurrence')}
                  helperText={errorsFor('recurrence')}
                  onChange={(ev) =>
                    setForm((prev) => ({
                      ...prev,
                      recurrence: ev.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value={DEFAULT_RECURRENCE_VALUE_KEY}>{t('components.filters.recurrence.selectRecurrence')}</MenuItem>
                  <MenuItem value="daily">{t('components.filters.recurrence.daily')}</MenuItem>
                  <MenuItem value="weekly">{t('components.filters.recurrence.weekly')}</MenuItem>
                  <MenuItem value="monthly">{t('components.filters.recurrence.monthly')}</MenuItem>
                  <MenuItem value="yearly">{t('components.filters.recurrence.yearly')}</MenuItem>
                </TextField>
              )}

              {availableFilters?.includes('time') && (
                <>
                  <DateTimePicker
                    label={t('components.datePicker.startDate')}
                    value={pickerStartDate}
                    onChange={onChangeStartDate}
                    open={openPeriodFrom}
                    onOpen={() => setOpenStartDate(true)}
                    onClose={() => setOpenStartDate(false)}
                    slots={{
                      inputAdornment: customStartPicker,
                    }}
                    slotProps={{
                      textField: {
                        size: 'small',
                        error: hasErrors('periodFrom'),
                        helperText: errorsFor('periodFrom'),
                                                style: comboStyle,

                      },
                    }}
                    format={t('components.datePicker.dateTimeFormat')}
                  />
                  <DateTimePicker
                    label={t('components.datePicker.endDate')}
                    value={pickerEndDate}
                    onChange={onChangeEndDate}
                    open={openPeriodTo}
                    onOpen={() => setOpenEndDate(true)}
                    onClose={() => setOpenEndDate(false)}
                    format={t('components.datePicker.dateTimeFormat')}
                    slots={{
                      inputAdornment: customEndPicker,
                    }}
                    slotProps={{
                      textField: {
                        size: 'small',
                        error: hasErrors('periodTo'),
                        style: comboStyle,
                        helperText: errorsFor('periodTo')
                        
                      }
                    }}
                  />
                          {isManual && isAutomated && enableScheduledFeature && (

                  <TimeZoneAccordion
                    value={timezone}
                    onChange={setTimezone}
                  />
                          )}
                </>
              )}
            </AccordionDetails>
          </Accordion>
        )}
        {hasLocationFilters && (
          <Accordion
            elevation={0}
            defaultExpanded
            square
            disableGutters
            sx={{ padding: 0, border: 0, margin: 0 }}
            onClick={() => setTimeout(fireResize, 250)}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ padding: 0, border: 0, margin: 0 }}
              aria-controls="areaFilters-content"
              id="areaFilters-header"
            >
              <Typography sx={subHeaderStyle} variant="h3">
                {t('components.filters.location')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 0, border: 0, margin: 0, marginBottom: 1 }}>
              {availableFilters?.includes('locationArea') && (
                <TextField
                  size="small"
                  select
                  value={
                    form.locationArea && formData?.locationAreas?.some(la => la.id === form.locationArea.id)
                      ? JSON.stringify(form.locationArea)
                      : 'all'
                  }
                  label={t('components.filters.locationArea')}
                  id="locationArea-label"
                  sx={comboStyle}
                  error={hasErrors('locationArea')}
                  helperText={errorsFor('locationArea')}
                  onChange={(ev) =>
                    setForm((prevForm) => ({
                      ...prevForm,
                      locationArea: ev.target.value === 'all' ? undefined : JSON.parse(ev.target.value),
                    }))
                  }
                  style={{ marginBottom: 16 }}
                  SelectProps={{ MenuProps: selectMenuScrollProps }}
                >
                  <MenuItem value="all" divider sx={stickyAllSelectMenuItemSx}>
                    {t('components.filters.allLocationAreas')}
                  </MenuItem>
                  {formData &&
                    formData.locationAreas &&
                    formData.locationAreas.map((la) => (
                      <MenuItem value={JSON.stringify(la)} key={la.id}>
                        {la.name}
                      </MenuItem>
                    ))}
                </TextField>
              )}

              {availableFilters?.includes('location') && (
                <TextField
                  size="small"
                  select
                  value={form.location ? JSON.stringify(form.location) : 'all'}
                  label={t('components.filters.location')}
                  id="location-label"
                  sx={comboStyle}
                  error={hasErrors('location')}
                  helperText={errorsFor('location')}
                  onChange={(ev) =>
                    setForm((prevForm) => ({
                      ...prevForm,
                      location: ev.target.value === 'all' ? undefined : JSON.parse(ev.target.value),
                    }))
                  }
                >
                  <MenuItem value="all" divider sx={stickyAllSelectMenuItemSx}>
                    {t('components.filters.allLocations')}
                  </MenuItem>
                  {formData &&
                    formData.locations &&
                    formData.locations.map((location) => (
                      <MenuItem value={JSON.stringify(location)} key={location.id}>
                        {location.name}
                      </MenuItem>
                    ))}
                </TextField>
              )}
            </AccordionDetails>
          </Accordion>
        )}
        {hasAdvancedFilters && (
          <Accordion
            elevation={0}
            square
            disableGutters
            sx={{ padding: 0, border: 0, margin: 0 }}
            onClick={() => setTimeout(fireResize, 250)}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="advancedFilters-content"
              sx={{ padding: 0, border: 0, margin: 0 }}
              id="advancedFilters-header"
            >
              <Typography sx={subHeaderStyle} variant="h3">
                {t('components.filters.advanced')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 0, border: 0, margin: 0 }}>

              {availableFilters?.includes('device') && (
                  <TextField
                  size="small"
                  select
                  value={form.device ? JSON.stringify(form.device) : 'all'}
                  label={t('components.filters.device')}
                  id="device-label"
                  sx={comboStyle}
                  error={hasErrors('device')}
                  helperText={errorsFor('device')}
                  onChange={(ev) =>
                    setForm((prevForm) => ({
                      ...prevForm,
                      device: ev.target.value === 'all' ? undefined : JSON.parse(ev.target.value),
                    }))
                  }
                  SelectProps={{ MenuProps: selectMenuScrollProps }}
                >
                  <MenuItem value="all" divider sx={stickyAllSelectMenuItemSx}>
                    {t('components.filters.allDevices')}
                  </MenuItem>
                  {formData &&
                    formData.devices &&
                    formData.devices.map((device) => (
                      <MenuItem value={JSON.stringify(device)} key={device.id}>
                        {device.name}
                      </MenuItem>
                    ))}
                </TextField>
              )}

              {availableFilters?.includes('deviceGroup') && (
                <TextField
                  size="small"
                  select
                  value={form.deviceGroup ? JSON.stringify(form.deviceGroup) : 'all'}
                  label={t('components.filters.deviceGroup')}
                  id="deviceGroup-label"
                  sx={comboStyle}
                  onChange={(ev) =>
                    setForm((prevForm) => ({
                      ...prevForm,
                      deviceGroup: ev.target.value === 'all' ? undefined : JSON.parse(ev.target.value),
                    }))
                  }
                  SelectProps={{ MenuProps: selectMenuScrollProps }}
                >
                  <MenuItem value="all" divider sx={stickyAllSelectMenuItemSx}>
                    {t('components.filters.allDeviceGroups')}
                  </MenuItem>
                  {formData && formData.deviceGroups &&
                    formData.deviceGroups.map((dg) => (
                      <MenuItem value={JSON.stringify(dg)} key={dg.id}>
                        {t(`components.filters.deviceGroups.${dg.name}`)}
                      </MenuItem>
                    ))}
                </TextField>
              )}

              {availableFilters?.includes('deviceType') && (
                <TextField
                  size="small"
                  select
                  value={form.deviceType ? JSON.stringify(form.deviceType) : 'all'}
                  label={t('components.filters.deviceType')}
                  id="deviceType-label"
                  sx={comboStyle}
                  onChange={(ev) =>
                    setForm((prevForm) => ({
                      ...prevForm,
                      deviceType: ev.target.value === 'all' ? undefined : JSON.parse(ev.target.value),
                    }))
                  }
                  SelectProps={{ MenuProps: selectMenuScrollProps }}
                >
                  <MenuItem value="all" divider sx={stickyAllSelectMenuItemSx}>
                    {t('components.filters.allDeviceTypes')}
                  </MenuItem>
                  {formData && formData.deviceTypes &&
                    formData.deviceTypes.map((dt) => (
                      <MenuItem value={JSON.stringify(dt)} key={dt.id}>
                        {t(`components.filters.deviceTypes.${dt.name}`)}
                      </MenuItem>
                    ))}
                </TextField>
              )}
              
              {availableFilters?.includes('paymentMethod') && (
                <TextField
                  size="small"
                  select
                  value={
                    form.tenderType ? JSON.stringify(form.tenderType) : 'all'
                  }
                  label={t('components.filters.paymentMethod')}
                  id="paymentMethod-label"
                  sx={{ ...comboStyle }}
                  error={hasErrors('paymentMethod')}
                  helperText={errorsFor('paymentMethod')}
                  onChange={(ev) =>
                    setForm((prevForm) => ({
                      ...prevForm,
                      tenderType: ev.target.value === 'all' ? undefined : JSON.parse(ev.target.value),
                    }))
                  }
                  SelectProps={{ MenuProps: selectMenuScrollProps }}
                >
                  <MenuItem value="all" divider sx={stickyAllSelectMenuItemSx}>
                    {t('components.filters.allPaymentMethods')}
                  </MenuItem>
                  {formData &&
                    formData.tenderTypes &&
                    formData.tenderTypes.map((pm) => (
                      <MenuItem value={JSON.stringify(pm)} key={pm.id}>
                        <ListItemText
                          sx={{
                            margin: 0,
                            float: 'left',
                          }}
                        >
                          {pm.name}
                        </ListItemText>
                        {pm.description && pm.description.trim() !== pm.name.trim() && (
                          <Typography
                            component="span"
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              marginTop: '2px',
                              float: 'right',
                            }}
                          >
                            {pm.description}
                          </Typography>
                        )}
                      </MenuItem>
                    ))}
                </TextField>
              )}

              {availableFilters?.includes('staff') && (
                <TextField
                  size="small"
                  select
                  value={form.staff ? JSON.stringify(form.staff) : 'all'}
                  label={t('components.filters.staff')}
                  id="staff-label"
                  sx={comboStyle}
                  error={hasErrors('staff')}
                  helperText={errorsFor('staff')}
                  onChange={(ev) =>
                    setForm((prevForm) => ({
                      ...prevForm,
                      staff: ev.target.value === 'all' ? undefined : JSON.parse(ev.target.value),
                    }))
                  }
                  SelectProps={{ MenuProps: selectMenuScrollProps }}
                >
                  <MenuItem value="all" divider sx={stickyAllSelectMenuItemSx}>
                    {t('components.filters.allStaff')}
                  </MenuItem>
                  {formData &&
                    formData.staff &&
                    formData.staff.map((sta) => (
                      <MenuItem value={JSON.stringify(sta)} key={sta.id}>
                        {sta.name}
                      </MenuItem>
                    ))}
                </TextField>
              )}
            </AccordionDetails>
          </Accordion>
        )}
        <Accordion
          elevation={0}
          square
          disableGutters
          sx={{ padding: 0, border: 0, margin: 0 }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="fileDownloadInfo-content"
            sx={{ padding: 0, border: 0, margin: 0 }}
            id="fileDownloadInfo-header"
          >
            <Typography sx={subHeaderStyle} variant="h3">
              {t('screens.dataDownloader.downloadedFile')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ padding: 0, border: 0, margin: 0 }}>
            <TextField
              fullWidth
              variant="outlined"
              label={t('screens.dataDownloader.fileType')}
              value=".CSV (Comma-Separated Values)"
              InputProps={{
                readOnly: true,
              }}
              sx={{ marginTop: '8px' }}
            />
          </AccordionDetails>
        </Accordion>
      </Grid>
    </TitledDrawer>
  );
};

export default GenerateReportOverviewPanel;
